import React, { Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import Layout from '../Layout/index';
import MainWrapper from './MainWrapper';

/* Every page is code-split into its own chunk so the initial download only
 * contains the shell + the visited page. If a chunk fails to load (typically
 * because a deploy replaced the hashed files under a long-lived tab), reload
 * the page once to pick up the new build instead of white-screening. */
const lazyLoad = importFn => React.lazy(() => importFn()
  .then((module) => {
    sessionStorage.removeItem('chunk_load_failed');
    return module;
  })
  .catch((error) => {
    if (!sessionStorage.getItem('chunk_load_failed')) {
      sessionStorage.setItem('chunk_load_failed', '1');
      window.location.reload();
    }
    throw error;
  }));

// Main Dashboard
const Dashboard = lazyLoad(() => import('../Dashboard'));
const ImportPage = lazyLoad(() => import('../ImportPage'));

// Analytics
const Analytics = lazyLoad(() => import('../Analytics'));

// Campaigns
const CreateCampaignPage = lazyLoad(() => import('../Campaigns/Create'));
const EditCampaignPage = lazyLoad(() => import('../Campaigns/Edit'));
const CampaignsPage = lazyLoad(() => import('../Campaigns/List'));

// Conversations
const ChatPage = lazyLoad(() => import('../Chat'));

// Investor Base
const ExternalInvestorsList = lazyLoad(() => import('../ExternalInvestors'));
const ExternalInvestorPage = lazyLoad(() => import('../ExternalInvestorPage'));

// Brokers
const ManagedStartupsList = lazyLoad(() => import('../ManagedStartups'));

// Startup Pages
const FinancialsPage = lazyLoad(() => import('../FinancialsPage/Summary'));
const StartupDeckPage = lazyLoad(() => import('../DeckPage/Startup'));
const StartupFinancialsPage = lazyLoad(() => import('../FinancialsPage/Startup'));
const SummaryPage = lazyLoad(() => import('../SummaryPage'));
const SummaryDeckPage = lazyLoad(() => import('../DeckPage/Summary'));

// Investor Community
const InvestorsPage = lazyLoad(() => import('../InternalInvestors'));
const InvestorPage = lazyLoad(() => import('../InternalInvestorPage'));
const NotificationsPage = lazyLoad(() => import('../NotificationsPage'));
const ProfileSettings = lazyLoad(() => import('../ProfileSettings'));

// Investor Pages
const InvestorProfile = lazyLoad(() => import('../MyInvestorProfile'));

// Tracking Links
const LinksPage = lazyLoad(() => import('../Links'));

// Resources
const ResourcesPage = lazyLoad(() => import('../ResourcesPage'));
const PartnershipsPage = lazyLoad(() => import('../PartnershipsPage'));

// Startup Community
const StartupsList = lazyLoad(() => import('../Startups'));
const StartupPage = lazyLoad(() => import('../StartupPage'));
const StartupProfile = lazyLoad(() => import('../MyStartupProfile'));

// Warm Introductions Page
const LinkedinIntroPage = lazyLoad(() => import('../LinkedinWarmIntroPage'));

// Founders Community
const Founders = lazyLoad(() => import('../Founders'));
const FounderPage = lazyLoad(() => import('../FounderPage'));
const FounderProfile = lazyLoad(() => import('../MyFounderProfile'));

// Debt Page
const Boundless = lazyLoad(() => import('../Boundless'));
const DebtRedirect = lazyLoad(() => import('../Boundless/DebtRedirect'));

// Shown while a page chunk downloads (same spinner as the in-page loaders)
const PageLoader = () => (
  <div className="load inload">
    <div className="load__icon-wrap">
      <svg className="load__icon">
        <path fill="#4ce1b6" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
      </svg>
    </div>
  </div>
);

const MyProfiles = () => (
  <Switch>
    <Route path="/myProfile/investor" component={InvestorProfile} />
    <Route path="/myProfile/company" component={StartupProfile} />
    <Route path="/myProfile/founder" component={FounderProfile} />
    <Route path="/myProfile/settings" component={ProfileSettings} />
  </Switch>
);

const WrappedCampaignsRoutes = () => (
  <Switch>
    <Route exact path="/campaigns/" component={CampaignsPage} />
    <Route path="/campaigns/create" component={CreateCampaignPage} />
    <Route path="/campaigns/edit/:id" component={EditCampaignPage} />
    {/* <Route path="/campaigns/:id" component={SummaryPage} /> */}
  </Switch>
);

const WrappedStartupRoutes = () => (
  <Switch>
    <Route path="/startup/deck/:id" component={StartupDeckPage} />
    <Route path="/startup/financials/:id" component={StartupFinancialsPage} />
    <Route path="/startup/:id" component={StartupPage} />
    <Route path="/startup/myCompany" component={StartupPage} />
  </Switch>
);

const WrappedFounderRoutes = () => (
  <Switch>
    <Route path="/founder/:id" component={FounderPage} />
    <Route path="/founder/myProfile" component={FounderPage} />
  </Switch>
);

const wrappedPrivateRoutes = () => (
  <div>
    <Layout />
    <div className="container__wrap">
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/dashboard" component={Dashboard} />
          <Route path="/analytics" component={Analytics} />
          <Route path="/import" component={ImportPage} />
          <Route path="/campaigns" component={WrappedCampaignsRoutes} />
          <Route path="/inbox/:id?" component={ChatPage} />
          <Route path="/investors" component={InvestorsPage} />
          <Route path="/investor/:id" component={InvestorPage} />
          <Route path="/myProfile" component={MyProfiles} />
          <Route path="/notifications" component={NotificationsPage} />
          <Route path="/investors-db" component={ExternalInvestorsList} />
          <Route path="/investor-db/:id" component={ExternalInvestorPage} />
          <Route path="/links" component={LinksPage} />
          <Route path="/resources" component={ResourcesPage} />
          <Route path="/partnerships" component={PartnershipsPage} />
          <Route path="/managed_startups" component={ManagedStartupsList} />
          <Route path="/startup" component={WrappedStartupRoutes} />
          <Route path="/founders" component={Founders} />
          <Route path="/founder" component={WrappedFounderRoutes} />
          <Route path="/startups" component={StartupsList} />
          <Route path="/warm-intro" component={LinkedinIntroPage} />
          <Route path="/debt/redirect" component={DebtRedirect} />
          <Route path="/debt" component={Boundless} />
          <Route
            path="/logout"
            component={() => {
              sessionStorage.clear();
              localStorage.clear();
              window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/logout`;
              return null;
            }}
          />
          <Route
            exact
            path="/"
            component={Dashboard}
          />
          <Route
            component={() => {
              window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/404`;
              return null;
            }}
          />
        </Switch>
      </Suspense>
    </div>
  </div>
);

const wrappedExternalRoutes = () => (
  <div>
    <Layout />
    <div className="container__wrap">
      <Switch>
        <Route
          path="/ext/blog"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/blog`;
            return null;
          }}
        />
        <Route
          path="/ext/terms_and_conditions"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/terms_and_conditions`;
            return null;
          }}
        />
        <Route
          path="/ext/landing_page"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}`;
            return null;
          }}
        />
        <Route
          path="/ext/pricing"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/pricing`;
            return null;
          }}
        />
        {/* The plan page that counts the founder's waiting emails (UserController::paywall) */}
        <Route
          path="/ext/paywall"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/paywall?ready=1`;
            return null;
          }}
        />
        <Route
          path="/ext/pricing/investor"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/pricing/investor`;
            return null;
          }}
        />
        <Route
          path="/ext/contact"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/contact`;
            return null;
          }}
        />
        <Route
          path="/ext/register/investor"
          component={() => {
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/register/investor`;
            return null;
          }}
        />
        <Route
          path="/ext/payment/process"
          component={() => {
            const user = JSON.parse(localStorage.getItem('user'));
            window.location.href = `/payment/${user.plan}?process=true`;
            return null;
          }}
        />
      </Switch>
    </div>
  </div>
);

const wrappedPublicRoutes = () => (
  <div className="layout layout__summary">
    <div className="container__wrap container__summary">
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/summary/deck/:id/:extInvId?" component={SummaryDeckPage} />
          <Route path="/summary/financials/:id/:extInvId?" component={FinancialsPage} />
          <Route path="/summary/:id/:extInvId?" component={SummaryPage} />
        </Switch>
      </Suspense>
    </div>
  </div>
);

const Router = loggedIn => (
  <MainWrapper>
    <main>
      <Switch>
        <Route path="/summary" component={wrappedPublicRoutes} />
        <Route path="/ext" component={wrappedExternalRoutes} />
        {loggedIn && (
          <Route path="/" component={wrappedPrivateRoutes} />
        )}
      </Switch>
    </main>
  </MainWrapper>
);

export default Router;
